import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useFarmProducts } from '@/components/pond/page/data';
import { useApplicationBundles } from '@/hooks/useApplicationBundles';
import { api } from '@/lib/api';
import { saveFailureMessage } from '@/lib/apiErrors';
import { slugify, type ApplicationBundle, type BundleItem } from '@/lib/applicationBundles';

type Kind = 'per_hectare' | 'per_lead' | 'typed';

interface DraftItem {
  product_id: string;
  kind: Kind;
  value: string;
}
interface DraftBundle {
  id: string | null;
  name: string;
  items: DraftItem[];
}

const KIND_LABEL: Record<Kind, string> = {
  per_hectare: 'per hectare of pond',
  per_lead: '× the first product',
  typed: 'typed each time',
};

function toDraft(b: ApplicationBundle): DraftBundle {
  return {
    id: b.id,
    name: b.name,
    items: b.items.map((i) => ({
      product_id: i.product_id,
      kind: i.typed ? 'typed' : i.per_lead !== undefined ? 'per_lead' : 'per_hectare',
      value: i.typed ? '' : String(i.per_hectare ?? i.per_lead ?? ''),
    })),
  };
}

function problem(b: DraftBundle): string | null {
  if (!b.name.trim()) return 'Give the bundle a name';
  if (b.items.length < 2) return 'A bundle needs at least 2 products';
  const seen = new Set<string>();
  for (const [index, item] of b.items.entries()) {
    if (!item.product_id) return 'Choose a product for every line';
    if (seen.has(item.product_id)) return 'A product can appear only once';
    seen.add(item.product_id);
    if (item.kind === 'typed') {
      if (index !== 0) return 'Only the first product can be typed';
      continue;
    }
    if (item.kind === 'per_lead' && index === 0) return 'The first product sets the amounts for the rest';
    if (!/^\d+(\.\d+)?$/.test(item.value.trim()) || Number(item.value) <= 0) return 'Doses must be numbers above 0';
  }
  return null;
}

function toApi(drafts: DraftBundle[]): ApplicationBundle[] {
  const used = new Set<string>();
  return drafts.map((b) => {
    let id = b.id ?? slugify(b.name);
    for (let n = 2; used.has(id); n++) id = `${slugify(b.name)}-${n}`.slice(0, 40);
    used.add(id);
    const items: BundleItem[] = b.items.map((i) =>
      i.kind === 'typed' ? { product_id: i.product_id, typed: true } : { product_id: i.product_id, [i.kind]: Number(i.value) }
    );
    return { id, name: b.name.trim(), items };
  });
}

/** Owner editor for the farm's application bundles (#372): named recipes of farm products. */
export function ApplicationBundlesEditor({ farmId, canEdit }: { farmId: string; canEdit: boolean }) {
  const saved = useApplicationBundles(farmId);
  const { data: products = [] } = useFarmProducts(farmId);
  // Re-key on the server's version so a save (or another owner's) resets the draft.
  return <Editor key={JSON.stringify(saved)} farmId={farmId} saved={saved} canEdit={canEdit} products={products.filter((p) => p.is_active)} />;
}

function Editor({
  farmId,
  saved,
  canEdit,
  products,
}: {
  farmId: string;
  saved: ApplicationBundle[];
  canEdit: boolean;
  products: Array<{ id: string; name: string; unit: string }>;
}) {
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<DraftBundle[]>(() => saved.map(toDraft));
  const dirty = JSON.stringify(drafts) !== JSON.stringify(saved.map(toDraft));
  const problems = drafts.map(problem);

  const save = useMutation({
    mutationFn: () => api.put(`/api/v1/farms/${farmId}/settings`, { application_bundles: toApi(drafts) }),
    onSuccess: () => {
      toast.success('Bundles saved');
      queryClient.invalidateQueries({ queryKey: ['farm', farmId] });
    },
  });

  const edit = (i: number, fn: (b: DraftBundle) => DraftBundle) => setDrafts((ds) => ds.map((b, j) => (j === i ? fn(b) : b)));
  const unitOf = (id: string) => products.find((p) => p.id === id)?.unit;
  const field = 'min-h-[44px] rounded-lg border border-gray-300 bg-white px-2 text-sm disabled:bg-gray-50 disabled:text-gray-500';

  return (
    <Card>
      <Card.Header>
        <Card.Title>Application bundles</Card.Title>
      </Card.Header>
      <Card.Body>
        <p className="mb-3 text-sm text-gray-500">
          Products applied together, e.g. a fermentation mix dosed per hectare, or a probiotic with molasses in proportion. Entering
          one saves a normal application for each product, with amounts scaled by the pond's area.
          {!canEdit && ' Only a Farm Owner can change these.'}
        </p>
        <div className="space-y-4" data-bundles>
          {drafts.map((b, i) => (
            <div key={b.id ?? `new-${i}`} className="rounded-lg border border-gray-200 p-3" data-bundle-editor>
              <div className="flex items-center gap-2">
                <input
                  className={`${field} flex-1`}
                  value={b.name}
                  disabled={!canEdit}
                  placeholder="Bundle name, e.g. 10-10-10"
                  aria-label={`Bundle ${i + 1} name`}
                  onChange={(e) => edit(i, (x) => ({ ...x, name: e.target.value }))}
                />
                {canEdit && (
                  <button type="button" className="min-h-[44px] min-w-[44px] px-2 text-sm font-semibold text-red-700" onClick={() => setDrafts((ds) => ds.filter((_, j) => j !== i))}>
                    Remove
                  </button>
                )}
              </div>
              <ul className="mt-2 space-y-2">
                {b.items.map((item, k) => (
                  <li key={k} className="flex flex-wrap items-center gap-2">
                    <select
                      className={`${field} min-w-[8rem] flex-1`}
                      value={item.product_id}
                      disabled={!canEdit}
                      aria-label={`Bundle ${i + 1} product ${k + 1}`}
                      onChange={(e) => edit(i, (x) => ({ ...x, items: x.items.map((it, m) => (m === k ? { ...it, product_id: e.target.value } : it)) }))}
                    >
                      <option value="">Choose a product</option>
                      {item.product_id && !products.some((p) => p.id === item.product_id) && <option value={item.product_id}>(inactive product)</option>}
                      {products.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({p.unit})
                        </option>
                      ))}
                    </select>
                    <select
                      className={field}
                      value={item.kind}
                      disabled={!canEdit}
                      aria-label={`Bundle ${i + 1} dose ${k + 1} kind`}
                      onChange={(e) => edit(i, (x) => ({ ...x, items: x.items.map((it, m) => (m === k ? { ...it, kind: e.target.value as Kind } : it)) }))}
                    >
                      {(['per_hectare', 'per_lead', 'typed'] as Kind[])
                        .filter((kind) => (kind === 'typed' ? k === 0 : kind === 'per_lead' ? k > 0 : true))
                        .map((kind) => (
                          <option key={kind} value={kind}>
                            {KIND_LABEL[kind]}
                          </option>
                        ))}
                    </select>
                    {item.kind !== 'typed' && (
                      <span className="flex items-center gap-1 text-sm text-gray-600">
                        <input
                          className={`${field} w-24 tabular-nums`}
                          inputMode="decimal"
                          value={item.value}
                          disabled={!canEdit}
                          aria-label={`Bundle ${i + 1} dose ${k + 1}`}
                          onChange={(e) => edit(i, (x) => ({ ...x, items: x.items.map((it, m) => (m === k ? { ...it, value: e.target.value } : it)) }))}
                        />
                        {item.kind === 'per_hectare' ? `${unitOf(item.product_id) ?? ''}/ha` : '×'}
                      </span>
                    )}
                    {canEdit && b.items.length > 2 && (
                      <button type="button" className="min-h-[44px] min-w-[44px] px-2 text-xs font-semibold text-red-700" onClick={() => edit(i, (x) => ({ ...x, items: x.items.filter((_, m) => m !== k) }))}>
                        ✕
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              {canEdit && b.items.length < 8 && (
                <button type="button" className="mt-2 min-h-[44px] text-sm font-semibold text-primary-600" onClick={() => edit(i, (x) => ({ ...x, items: [...x.items, { product_id: '', kind: 'per_hectare', value: '' }] }))}>
                  + Add a product
                </button>
              )}
              {problems[i] && (
                <p role="alert" className="mt-1 text-xs text-red-600">
                  {problems[i]}
                </p>
              )}
            </div>
          ))}
          {drafts.length === 0 && <p className="text-sm text-gray-500">No bundles yet.</p>}
        </div>
        {canEdit && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              className="min-h-[44px] text-sm font-semibold text-primary-600"
              onClick={() =>
                setDrafts((ds) => [
                  ...ds,
                  { id: null, name: '', items: [{ product_id: '', kind: 'per_hectare', value: '' }, { product_id: '', kind: 'per_hectare', value: '' }] },
                ])
              }
            >
              + New bundle
            </button>
            <div className="flex items-center gap-3">
              {save.isError && (
                <span role="alert" className="text-sm text-red-600">
                  Not saved: {saveFailureMessage(save.error, 'try again')}
                </span>
              )}
              <Button onClick={() => save.mutate()} disabled={!dirty || problems.some(Boolean) || save.isPending} loading={save.isPending} className="min-h-[44px]">
                Save bundles
              </Button>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
